import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";

export type DestinationOption = {
  key: string;
  label: string;
  /** When true, option is styled as the order customer hop. */
  isCustomer?: boolean;
};

type SearchableDestinationSelectProps = {
  valueKey: string;
  displayLabel?: string;
  options: DestinationOption[];
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  onChange: (key: string) => void;
};

/**
 * Destination dropdown with search inside the open menu.
 * Customer options show name only and are visually highlighted.
 * Selected value shows name only (no address tooltip).
 * Explicit colors so OrCA theme does not grey out the trigger.
 */
export default function SearchableDestinationSelect({
  valueKey,
  displayLabel,
  options,
  placeholder = "Select destination",
  isLoading = false,
  isDisabled = false,
  onChange,
}: SearchableDestinationSelectProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  const selectedOptionLabel = options.find((o) => o.key === valueKey)?.label;
  const buttonLabel =
    displayLabel ||
    selectedOptionLabel ||
    (isLoading ? "Loading..." : placeholder);
  const selectedIsCustomer =
    valueKey.startsWith("CUSTOMER:") ||
    options.find((o) => o.key === valueKey)?.isCustomer === true;

  const triggerBg = selectedIsCustomer ? "teal.50" : "white";
  const triggerBorder = selectedIsCustomer ? "teal.300" : "gray.200";
  const triggerColor = selectedIsCustomer ? "teal.800" : "gray.800";

  return (
    <Popover
      isOpen={isDisabled ? false : isOpen}
      onOpen={() => {
        if (!isDisabled) onOpen();
      }}
      onClose={onClose}
      placement="bottom-start"
      matchWidth
      isLazy
    >
      <Box w="100%">
        <PopoverTrigger>
          <Button
            w="100%"
            h="auto"
            minH="40px"
            py={2}
            variant="outline"
            justifyContent="space-between"
            alignItems="flex-start"
            fontWeight="normal"
            bg={triggerBg}
            color={triggerColor}
            borderWidth="1px"
            borderColor={triggerBorder}
            _hover={{ bg: selectedIsCustomer ? "teal.100" : "gray.50" }}
            _active={{ bg: selectedIsCustomer ? "teal.100" : "gray.100" }}
            _disabled={{
              bg: triggerBg,
              color: "gray.500",
              borderColor: triggerBorder,
              opacity: 0.7,
              cursor: "not-allowed",
            }}
            rightIcon={<ChevronDownIcon mt={0.5} flexShrink={0} color="gray.600" />}
            // Keep usable while suppliers load — only hard-disable via prop
            isDisabled={isDisabled}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            textAlign="left"
            whiteSpace="normal"
          >
            <Text
              flex={1}
              textAlign="left"
              whiteSpace="normal"
              wordBreak="break-word"
              lineHeight="short"
              fontWeight={selectedIsCustomer ? "600" : "normal"}
              color={isLoading && !valueKey ? "gray.500" : triggerColor}
            >
              {buttonLabel}
            </Text>
          </Button>
        </PopoverTrigger>
      </Box>
      <Portal>
        <PopoverContent
          w="100%"
          maxW="420px"
          zIndex={1500}
          bg="white"
          color="gray.800"
          borderColor="gray.200"
        >
          <PopoverBody p={2} bg="white">
            <Input
              ref={searchRef}
              size="sm"
              mb={2}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="white"
              color="gray.800"
              borderColor="gray.200"
              _placeholder={{ color: "gray.400" }}
            />
            <Box maxH="220px" overflowY="auto">
              <VStack align="stretch" spacing={0}>
                {isLoading && filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={2} py={2}>
                    Loading destinations...
                  </Text>
                ) : filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={2} py={2}>
                    No matches
                  </Text>
                ) : (
                  filtered.map((opt) => {
                    const isCustomer =
                      opt.isCustomer === true ||
                      opt.key.startsWith("CUSTOMER:");
                    const isSelected = opt.key === valueKey;
                    return (
                      <Box
                        key={opt.key}
                        as="button"
                        type="button"
                        textAlign="left"
                        px={2}
                        py={2}
                        borderRadius="md"
                        bg={
                          isSelected
                            ? isCustomer
                              ? "teal.100"
                              : "blue.50"
                            : isCustomer
                              ? "teal.50"
                              : "transparent"
                        }
                        borderWidth={isCustomer ? "1px" : "0"}
                        borderColor={isCustomer ? "teal.200" : "transparent"}
                        mb={isCustomer ? 1 : 0}
                        _hover={{
                          bg: isCustomer ? "teal.100" : "gray.100",
                        }}
                        onClick={() => {
                          onChange(opt.key);
                          onClose();
                        }}
                      >
                        <HStack
                          justify="space-between"
                          align="center"
                          spacing={2}
                        >
                          <Text
                            fontSize="sm"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            fontWeight={isCustomer ? "600" : "normal"}
                            color={isCustomer ? "teal.800" : "gray.800"}
                          >
                            {opt.label}
                          </Text>
                          {isCustomer && (
                            <Badge
                              colorScheme="teal"
                              fontSize="0.65rem"
                              flexShrink={0}
                            >
                              Customer
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </VStack>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
